import { Component, type ErrorInfo, type ReactNode } from 'react';

function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  return (
    error.name === 'ChunkLoadError' ||
    /Loading chunk [\w-]+ failed/i.test(error.message) ||
    /Failed to fetch dynamically imported module/i.test(error.message)
  );
}

type Props = { children: ReactNode };
type State = { hasChunkError: boolean };

const RELOAD_FLAG = 'mbot_chunk_reload_attempted';
// Guard window must be larger than the router's chunk import timeout (20s) so a
// chunk that keeps failing immediately after a reload can't reload-loop, while
// a genuine failure long afterward is still allowed to self-heal.
const RELOAD_GUARD_MS = 60000;

export class ChunkLoadErrorBoundary extends Component<Props, State> {
  state: State = { hasChunkError: false };

  static getDerivedStateFromError(error: Error): Partial<State> | null {
    if (isChunkLoadError(error)) {
      return { hasChunkError: true };
    }
    return null;
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    if (!isChunkLoadError(error)) {
      throw error;
    }
    const lastReload = Number(sessionStorage.getItem(RELOAD_FLAG) || 0);
    const now = Date.now();
    if (!lastReload || now - lastReload > RELOAD_GUARD_MS) {
      sessionStorage.setItem(RELOAD_FLAG, String(now));
      window.location.reload();
      return;
    }
    console.error('Chunk load failed after reload', error, info);
  }

  render() {
    if (this.state.hasChunkError) {
      return (
        <div style={{ padding: 24, textAlign: 'center' }}>
          <p>A new version may be available or the connection was interrupted.</p>
          <p>Please wait… attempting to recover.</p>
        </div>
      );
    }
    return this.props.children;
  }
}